/* eslint-disable jsx-a11y/no-static-element-interactions -- onClick handlers on div wrappers for event propagation control */
import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Drawer, Table, Empty } from 'antd-v5';
import moment from 'moment';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { getRequest } from '../../../../../core/apiRequests';
import ConversationWindow from '../../../../../components/Layout/chat/chat';
import {
  getClientChat,
  resetMessaging,
  setChatReviewLink,
  setClientAssociate,
} from '../../../../../reducers/messaging';
import { resetToday } from '../../../../../reducers/today';
import getClientPaymentStatus from '../../../../../components/Layout/GetClientPaymentStatus';
import MessageConfigToday from '../../../../../components/Routes/Today/children/MessageConfigToday';
import '../../../../../components/Routes/Today/today.css';

const AllSalesOpp = ({ auth }) => {
  const [showChat, setShowChat] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [isShown, setIsShown] = useState('');

  const salesName = useMemo(
    () => auth?.merchantConfig?.SALES_OPP_NAME || 'Sales Opportunities',
    [auth?.merchantConfig],
  );

  const { data: salesData, isFetching } = useQuery(
    ['allSalesOpp', page, pageSize],
    async () => {
      try {
        const reqObj = {
          params: ['today', auth.merchantId, 'getSalesOpps', auth.userId],
          query: {
            pageStart: page,
          },
        };
        const response = await getRequest(reqObj);
        if (response?.salesOpps) {
          return {
            data: response.salesOpps,
            salesStages: response.salesStages || [],
            // API doesn't return total count, estimate based on page size
            total: response.salesOpps.length < 25 ? (page - 1) * 25 + response.salesOpps.length : page * 25 + 1,
          };
        }
        return { data: [], salesStages: [], total: 0 };
      } catch (error) {
        console.error('Error fetching sales opportunities:', error);
        return { data: [], salesStages: [], total: 0 };
      }
    },
    {
      refetchOnWindowFocus: false,
      initialData: { data: [], salesStages: [], total: 0 },
      enabled: !!auth?.merchantId,
    },
  );

  const green = obj => {
    if (salesData?.data?.length > 0 && salesData?.salesStages?.length > 0) {
      const filteredSalesOpps =
        salesData.salesStages.filter(opp => opp.sales_pipeline_id === obj.sales_pipeline_id) || [];
      if (filteredSalesOpps.length > 0) {
        const small = filteredSalesOpps.reduce((prev, curr) => (prev.weight < curr.weight ? prev : curr));
        return small.id;
      }
    }
    return null;
  };

  const fullNameCheck = (first, last) => {
    if (last) {
      return `${first} ${last}`.trim();
    }
    return first;
  };

  const closeChat = () => {
    setShowChat(false);
    resetMessaging();
    resetToday();
  };

  const getReviewLink = async reviewStoreId => {
    if (reviewStoreId) {
      const reqObj = { params: ['chats', reviewStoreId, 'getReviewLink'] };
      const linkData = await getRequest(reqObj);
      if (linkData.length > 0) {
        setChatReviewLink(linkData[0].review_link);
      }
    }
  };

  const openChatModal = async client => {
    await getClientChat(client);
    await getReviewLink(client.store_id);
    setClientAssociate(client.associateCount);
    getClientPaymentStatus({ client, auth });
    setShowChat(true);
  };

  const columns = useMemo(
    () => [
      {
        title: 'Due Date',
        dataIndex: 'close_date',
        key: 'close_date',
        width: '12%',
        render: t => {
          if (t) {
            return moment(t, 'YYYYMMDD').format('MM/DD/YYYY');
          }
          return null;
        },
      },
      {
        title: 'Client Name',
        dataIndex: 'client_name',
        key: 'client_name',
        width: '15%',
        render: (text, record) => <Link to={`/Clients?client=${record.client_id}`}>{text}</Link>,
      },
      {
        title: 'Description',
        dataIndex: 'name',
        key: 'name',
        width: '20%',
        render: (text, record) => <Link to={`/Clients?client=${record.client_id}&oppId=${record.id}`}>{text}</Link>,
      },
      {
        title: 'Price Value',
        dataIndex: 'amount',
        key: 'amount',
        width: '10%',
        render: amount => {
          if (amount) {
            return `$${amount}`;
          }
          return '$0';
        },
      },
      {
        title: 'Stage',
        dataIndex: 'stage_name',
        key: 'stage_name',
        width: '12%',
        align: 'center',
        render: (text, record) => {
          if (record.weight === 0 || record.weight === 1) {
            return <span className="today-status-badge assigned">{text}</span>;
          }
          if (green(record) === record.sales_opp_stage_id) {
            return <span className="today-status-badge active">{text}</span>;
          }
          return <span className="today-status-badge in-progress">{text}</span>;
        },
      },
      {
        title: 'Created by',
        dataIndex: 'created_by',
        key: 'created_by',
        width: '12%',
        render: (_, record) => fullNameCheck(record.first_name, record.last_name),
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        width: '8%',
        align: 'center',
        render: (_, record) => (
          <div className="align-center pos-rel" style={{ maxHeight: '34px' }} onClick={e => e.stopPropagation()}>
            <MessageConfigToday
              keyVar={Math.floor(Math.random() * record.id)}
              msgObj={{ ...record, name: record.client_name, client_id: record.client_id }}
              setShowPopConfirm={openChatModal}
              initiateTemplates={openChatModal}
              setIsShown={setIsShown}
              isShown={isShown}
            />
          </div>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '11%',
        align: 'center',
        render: (_, record) => {
          const now = moment(new Date());
          if (!record.complete_date) {
            if (moment(now).isAfter(moment(record.close_date, 'YYYYMMDD'))) {
              return <span className="today-status-badge overdue">OVERDUE</span>;
            }
          }
          return null;
        },
      },
    ],
    [salesData?.salesStages, isShown],
  );

  return (
    <StandardLayoutV2
      title={salesName}
      breadcrumb={[{ path: '/', title: 'Today' }, { title: salesName }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <Table
        className="today-table"
        columns={columns}
        dataSource={salesData?.data || []}
        rowKey={record => record.id}
        loading={isFetching}
        pagination={{
          pageSize,
          current: page,
          total: salesData?.total || 0,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${salesName}`} />,
        }}
      />
      <Drawer
        styles={{
          body: { padding: 0, overflow: 'hidden' },
        }}
        closable={false}
        push={false}
        destroyOnClose
        width={750}
        open={showChat}
        onClose={closeChat}
      >
        <div style={{ height: '100vh' }}>
          <ConversationWindow setDrawerToggle={closeChat} chatIconColor="#EBEBEB" type="right" />
        </div>
      </Drawer>
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllSalesOpp);
